import React, { useContext, useState } from "react";
import Button from "../sharable/ActionButton";
import { ListProduct } from "../sharable/ListProduct";
import {
  Container,
  ExampleText,
  TextArea,
  LineCounter,
  ProductListWrapper,
  ProductListItemRow,
  RemoveProductButton,
} from "../../styles/ProductListProcessorStyles";
import useProductListProcessor from "../WorkSpace/ToolBarMenuViews/ProductsMenuView/hooks/useProductListProcessor";
import { convertProductsImagesToDataURL } from "../../services/Utils";
import { useOutletContext } from "react-router";
import WorkspaceContext from "../WorkSpace/WorkspaceContext";
import PremiumContentProtectiveLayer from "../sharable/PremiumContentProtectiveLayer";
import useProductListDraftInput from "../WorkSpace/ToolBarMenuViews/ProductsMenuView/hooks/useProductListDraftInput";
import ProductStyles from "../../OBJRep/ProductStyles";
import {
  MAX_PRODUCTS,
  MAX_PRODUCTS_BY_FORMAT,
} from "../WorkSpace/CanvasViewer/EncarteConstants";

const PRODUCT_LIST_INPUT_STORAGE_KEY = "encarte.productListInput";

export default function ProductListProcessor() {
  const { token, isAccountPremium } = useOutletContext();
  const { onProductsChange, styles } = useContext(WorkspaceContext);
  const maxInputLines = MAX_PRODUCTS_BY_FORMAT[styles?.format] || MAX_PRODUCTS;
  const {
    value: productInput,
    updateValue: updateProductInput,
    linesCount: typedLinesCount,
  } = useProductListDraftInput({
    storageKey: PRODUCT_LIST_INPUT_STORAGE_KEY,
    maxLines: maxInputLines,
  });
  const [productList, setProductList] = useState([]);
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);
  const [message, setMessage] = useState(null);
  const { processProductList, loading, error } = useProductListProcessor();

  const handleProcessProducts = async () => {
    setMessage(null);
    const inputLines = productInput.split(/\r?\n/).filter((l) => l.trim());
    if (inputLines.length === 0) {
      setMessage("Digite ao menos um produto.");
      return;
    }

    const processedProducts = await processProductList(productInput, token);
    if (!processedProducts) return;

    if (processedProducts.length === 0) {
      setMessage("Nenhum produto reconhecido. Confira os formatos acima.");
      return;
    }
    const unrecognized = inputLines.length - processedProducts.length;
    // Server silently drops unparsable (and duplicate) lines, so this is only a count estimate.
    const unrecognizedMessage =
      unrecognized > 0
        ? `${unrecognized} produto(s) não reconhecidos. Digite no formato correto.`
        : null;

    setIsDownloadingImages(true);
    try {
      const productsWithImages = await convertProductsImagesToDataURL(
        processedProducts,
        token,
      );
      // Server products come back with styles: null; onProductsChange's equal-length
      // shortcut (Workspace.jsx) skips style assignment, so it must be valid here already.
      const normalizedProducts = productsWithImages.map((p) => ({
        ...p,
        styles: p.styles || ProductStyles(),
      }));
      setProductList(normalizedProducts);

      const failedImagesCount = normalizedProducts.filter(
        (p) => p.imageURL && !p.displayUrl,
      ).length;
      setMessage(
        unrecognizedMessage ||
          (failedImagesCount > 0
            ? `${failedImagesCount} imagem(ns) não puderam ser baixadas. Troque-as manualmente.`
            : null),
      );
    } catch (err) {
      console.error(err);
      setMessage("Erro ao baixar imagens. Tente novamente.");
    } finally {
      setIsDownloadingImages(false);
    }
  };

  const handleAddAllProducts = () => {
    onProductsChange(productList);
    localStorage.removeItem(PRODUCT_LIST_INPUT_STORAGE_KEY);
    setProductList([]);
    updateProductInput("");
  };

  const handleRemoveProduct = (indexToRemove) => {
    setProductList((currentList) =>
      currentList.filter((_, index) => index !== indexToRemove),
    );
  };

  return (
    <Container>
      {!isAccountPremium && <PremiumContentProtectiveLayer />}
      <ExampleText>Digite um produto por linha. Formatos aceitos:</ExampleText>
      <ExampleText>• Preço simples: "Coca Cola 2L 9,99"</ExampleText>
      <ExampleText>• De/Por: "Arroz Namorado de 5,99 por 4,99"</ExampleText>
      <ExampleText>
        • Atacado/Varejo: "Sabonete Dove atacado 5,99 varejo 6,45"
      </ExampleText>
      <ExampleText>
        • Leve X Pague Y: "Flocão Bonomilho Leve 10 por 9,99"
      </ExampleText>

      <ExampleText style={{ color: "#f71515" }}>
        Adicionar produtos aqui irá apagar os produtos existentes no encarte.
      </ExampleText>
      <TextArea
        value={productInput}
        onChange={(e) => updateProductInput(e.target.value)}
        placeholder="Cole sua lista de produtos aqui..."
      />
      <LineCounter>
        {typedLinesCount}/{maxInputLines}
      </LineCounter>
      <Button
        text={
          loading
            ? "Processando..."
            : isDownloadingImages
              ? "Baixando imagens..."
              : productInput.length > 0
                ? "Processar Produtos"
                : "Digite sua lista"
        }
        action={handleProcessProducts}
        $fullWidth
        disabled={loading || isDownloadingImages}
        $NoBgColor
        color={"#fff"}
      />
      {(error || message) && <p style={{ color: "red" }}>{error || message}</p>}
      <ProductListWrapper>
        {productList?.map((product, index) => (
          <ProductListItemRow key={index}>
            <ListProduct product={product} action={() => {}} />
            <RemoveProductButton
              type="button"
              onClick={() => handleRemoveProduct(index)}
            >
              Remover
            </RemoveProductButton>
          </ProductListItemRow>
        ))}
        {productList?.length > 0 && (
          <Button
            text="Adicionar todos"
            action={handleAddAllProducts}
            $fullWidth
          />
        )}
      </ProductListWrapper>
    </Container>
  );
}
